import React from 'react';
import { Group, Box, Alert, Button } from '@mantine/core';
import { IconDeviceFloppy, IconArrowBackUp } from '@tabler/icons-react';
import { LoadingButton } from '../common/LoadingButton';

interface EntityDialogActionsProps {
    onCancel: () => void;
    onSave: () => void;
    onApply?: () => void;
    onDiscard?: () => void;
    loading?: boolean;
    isEditMode?: boolean;
    hasChanges?: boolean;
    disabled?: boolean;
    saveLabel?: string;
    createLabel?: string;
    applyLabel?: string;
    cancelLabel?: string;
    showApply?: boolean;
    schemaLoading?: boolean;
    isValid?: boolean;
    error?: string | null;
    borderPosition?: 'top' | 'bottom' | 'none';
}

export const EntityDialogActions: React.FC<EntityDialogActionsProps> = ({
    onCancel,
    onSave,
    onApply,
    onDiscard,
    loading = false,
    isEditMode = false,
    hasChanges = false,
    disabled = false,
    saveLabel,
    createLabel,
    applyLabel = 'Apply',
    cancelLabel = 'Cancel',
    showApply = true,
    schemaLoading = false,
    isValid = true,
    error = null,
    borderPosition = 'top'
}) => {
    const shouldShowApply = showApply && isEditMode && hasChanges && onApply;
    const saveDisabled = loading || disabled || schemaLoading || !isValid;
    const cancelDisabled = loading || (isEditMode && !hasChanges);
    const showSaveIcon = !isEditMode || hasChanges;

    const getSaveButtonText = () => {
        if (loading) {
            return isEditMode ? 'Saving...' : 'Creating...';
        }
        if (isEditMode) {
            return saveLabel || (hasChanges ? 'Save & Close' : 'Close');
        }
        return createLabel || 'Create';
    };

    const borderStyle = borderPosition === 'bottom'
        ? { borderBottom: '1px solid var(--mantine-color-default-border)' }
        : borderPosition === 'none'
            ? undefined
            : { borderTop: '1px solid var(--mantine-color-default-border)' };

    return (
        <Box px="md" py="sm" style={borderStyle}>
            {error && (
                <Alert color="red" mb="sm" w="100%">
                    {error}
                </Alert>
            )}
            <Group justify="flex-end" gap="sm">
                {onDiscard && hasChanges && isEditMode && (
                    <Button
                        onClick={onDiscard}
                        disabled={loading}
                        leftSection={<IconArrowBackUp size={16} />}
                        color="yellow"
                        variant="subtle"
                    >
                        Discard Changes
                    </Button>
                )}
                <Button
                    variant="default"
                    onClick={onCancel}
                    disabled={cancelDisabled}
                >
                    {cancelLabel}
                </Button>
                {shouldShowApply && (
                    <LoadingButton
                        onClick={onApply}
                        variant="outline"
                        loading={loading}
                        loadingText="Applying..."
                        icon={<IconDeviceFloppy size={16} />}
                        disabled={!isValid || loading || schemaLoading || disabled}
                    >
                        {applyLabel}
                    </LoadingButton>
                )}
                <LoadingButton
                    onClick={isEditMode && !hasChanges ? onCancel : onSave}
                    variant="filled"
                    loading={loading}
                    loadingText={isEditMode ? 'Saving...' : 'Creating...'}
                    icon={showSaveIcon ? <IconDeviceFloppy size={16} /> : undefined}
                    disabled={saveDisabled}
                >
                    {getSaveButtonText()}
                </LoadingButton>
            </Group>
        </Box>
    );
};
